import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export interface FeedbackOpenOptions {
  triggerType?: 'manual' | 'auto'
  triggerEvent?: string
  surveyKey?: string
}

interface FeedbackContextValue {
  isOpen: boolean
  openOptions: FeedbackOpenOptions | null
  /**
   * Changes on every open(). The toast resets its draft when this changes,
   * so a menu open cannot keep a parked auto session's answers.
   * Parking a session does not change it, so that draft is kept.
   */
  sessionId: number | null
  pageIds: Record<string, string>
  /**
   * True while at least one surface fills the bottom-right corner the
   * feedback toast would cover (survey charts, twin chat, analyst composer).
   */
  cornerOccupied: boolean
  open: (opts?: FeedbackOpenOptions) => void
  close: () => void
  setPageIds: (ids: Record<string, string>) => void
  claimCorner: () => number
  releaseCorner: (token: number) => void
}

const FeedbackCtx = createContext<FeedbackContextValue | null>(null)

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [openOptions, setOpenOptions] = useState<FeedbackOpenOptions | null>(null)
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [pageIds, setPageIdsState] = useState<Record<string, string>>({})
  const [cornerOccupied, setCornerOccupiedState] = useState(false)
  const nextSessionIdRef = useRef(0)
  const nextCornerTokenRef = useRef(0)
  const cornerClaimsRef = useRef<Set<number>>(new Set())

  const open = useCallback((opts?: FeedbackOpenOptions) => {
    nextSessionIdRef.current += 1
    setSessionId(nextSessionIdRef.current)
    setOpenOptions(opts ?? { triggerType: 'manual' })
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setOpenOptions(null)
    setSessionId(null)
  }, [])

  const setPageIds = useCallback((ids: Record<string, string>) => {
    setPageIdsState(ids)
  }, [])

  const claimCorner = useCallback(() => {
    nextCornerTokenRef.current += 1
    const token = nextCornerTokenRef.current
    cornerClaimsRef.current.add(token)
    setCornerOccupiedState(true)
    return token
  }, [])

  const releaseCorner = useCallback((token: number) => {
    cornerClaimsRef.current.delete(token)
    setCornerOccupiedState(cornerClaimsRef.current.size > 0)
  }, [])

  const value = useMemo(
    () => ({
      isOpen,
      openOptions,
      sessionId,
      pageIds,
      cornerOccupied,
      open,
      close,
      setPageIds,
      claimCorner,
      releaseCorner,
    }),
    [
      isOpen,
      openOptions,
      sessionId,
      pageIds,
      cornerOccupied,
      open,
      close,
      setPageIds,
      claimCorner,
      releaseCorner,
    ],
  )

  return <FeedbackCtx.Provider value={value}>{children}</FeedbackCtx.Provider>
}

export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackCtx)
  if (!ctx) throw new Error('useFeedback must be used within <FeedbackProvider>')
  return ctx
}

/**
 * Claim or release the toast corner for the lifetime of this surface.
 * Claims stack: one surface unmounting does not clear a claim still held
 * by another. The claim runs in a layout effect so it lands before timers:
 * a prompt that comes due in the same turn as opening Survey data or chat
 * must see the corner as taken and must not start its cooldown.
 * No-ops outside `FeedbackProvider` so report and chat screens can render
 * in tests and previews that do not mount the app chrome.
 */
export function useFeedbackCornerOccupied(occupied: boolean) {
  const ctx = useContext(FeedbackCtx)
  const claimCorner = ctx?.claimCorner
  const releaseCorner = ctx?.releaseCorner

  useLayoutEffect(() => {
    if (!occupied || !claimCorner || !releaseCorner) return
    const token = claimCorner()
    return () => releaseCorner(token)
  }, [occupied, claimCorner, releaseCorner])
}
